import ReceptionCheckInOutData, {
  IReceptionCheckInOutData
} from "@/types/clients/esquire/data/general/reception-check-in-out-data";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";

dayjs.extend(weekday);

type WeekdayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
type TimeType = "checkin" | "checkout";
type TimeField = "from" | "until";

export interface IReceptionTime {
  checkin: IReceptionCheckInOutData;
  checkout: IReceptionCheckInOutData;
}

export default class ReceptionTime {
  public checkin: ReceptionCheckInOutData
  public checkout: ReceptionCheckInOutData

  constructor(data: IReceptionTime) {
    this.checkin = new ReceptionCheckInOutData(data.checkin);
    this.checkout = new ReceptionCheckInOutData(data.checkout);
  }

  toDTO(): IReceptionTime {
    return {
      checkin: this.checkin.toDTO(),
      checkout: this.checkout.toDTO()
    };
  }

  static fromDTO(dto: IReceptionTime): ReceptionTime {
    return new ReceptionTime(dto);
  }

  public receptionCheckInFrom(dateString = ""): string {
    const date = dateString ? dayjs(dateString) : dayjs();
    const day = date.format("ddd").toLowerCase();

    switch (day) {
      case "mon":
        return this.checkin.mon.from;
      case "tue":
        return this.checkin.tue.from;
      case "wed":
        return this.checkin.wed.from;
      case "thu":
        return this.checkin.thu.from;
      case "fri":
        return this.checkin.fri.from;
      case "sat":
        return this.checkin.sat.from;
      case "sun":
        return this.checkin.sun.from;
      default:
        return "";
    }
  }

  public receptionCheckOutFrom(dateString = ""): string {
    const date = dateString ? dayjs(dateString) : dayjs();
    const day = date.format("ddd").toLowerCase();

    switch (day) {
      case "mon":
        return this.checkout.mon.from;
      case "tue":
        return this.checkout.tue.from;
      case "wed":
        return this.checkout.wed.from;
      case "thu":
        return this.checkout.thu.from;
      case "fri":
        return this.checkout.fri.from;
      case "sat":
        return this.checkout.sat.from;
      case "sun":
        return this.checkout.sun.from;
      default:
        return "";
    }
  }

  public receptionCheckOutUntil(dateString = ""): string {
    const date = dateString ? dayjs(dateString) : dayjs();
    const day = date.format("ddd").toLowerCase();

    switch (day) {
      case "mon":
        return this.checkout.mon.until;
      case "tue":
        return this.checkout.tue.until;
      case "wed":
        return this.checkout.wed.until;
      case "thu":
        return this.checkout.thu.until;
      case "fri":
        return this.checkout.fri.until;
      case "sat":
        return this.checkout.sat.until;
      case "sun":
        return this.checkout.sun.until;
      default:
        return "";
    }
  }

  receptionCheckInUntil(dateString = ""):string {
    const date = dateString ? dayjs(dateString) : dayjs();
    const day = date.format("ddd").toLowerCase();


    switch (day) {
      case "mon" :
        return this.checkin.mon.until;
      case "tue" :
        return this.checkin.tue.until;
      case "wed" :
        return this.checkin.wed.until;
      case "thu" :
        return this.checkin.thu.until;
      case "fri" :
        return this.checkin.fri.until;
      case "sat" :
        return this.checkin.sat.until;
      case "sun" :
        return this.checkin.sun.until;
    }

    return "";
  }

  private weekdayKey(dateString = ""): WeekdayKey {
    const date = dateString ? dayjs(dateString) : dayjs();
    const day = date.format("ddd").toLowerCase();

    switch (day) {
      case "mon":
      case "tue":
      case "wed":
      case "thu":
      case "fri":
      case "sat":
      case "sun":
        return day;
      default:
        return "mon";
    }
  }

  private timeForDay(type: TimeType, field: TimeField, dateString = ""): string {
    const day = this.weekdayKey(dateString);
    const data = this[type];

    switch (day) {
      case "mon":
        return data.mon[field];
      case "tue":
        return data.tue[field];
      case "wed":
        return data.wed[field];
      case "thu":
        return data.thu[field];
      case "fri":
        return data.fri[field];
      case "sat":
        return data.sat[field];
      case "sun":
        return data.sun[field];
    }
  }

  public checkInFrom(dateString = ""): string {
    return this.timeForDay("checkin", "from", dateString);
  }

  public checkInUntil(dateString = ""): string {
    return this.timeForDay("checkin", "until", dateString);
  }

  public checkOutFrom(dateString = ""): string {
    return this.timeForDay("checkout", "from", dateString);
  }

  public checkOutUntil(dateString = ""): string {
    return this.timeForDay("checkout", "until", dateString);
  }
}
